//Oct19 //src/compontns/FormSettings/FormSubmitSettings/ColumnDraftManager.jsx:
/* ColumnDraftManager.jsx – list + inline drawer
   -----------------------------------------------------------------
   2025-05-12  Re-built to mirror FormFieldSelectorComponent logic
   2025-05-15  +350 ms debounced persist of columnOrder/visibility
   2025-05-18  Added “more-vert” header menu
   2025-05-19  New-calculated-field workflow (row highlight + drawer)
   2025-05-20  Drawer now hosts <CalculatedFieldEditor />
   2025-05-24  Delete logic for calculated columns (local + Firestore)
   2025-05-25  🔄  Replaced window.confirm with ConfirmDeleteDialog
*/

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Switch,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Menu,
  Divider,
  Drawer,
} from '@mui/material';
import MoreVertIcon     from '@mui/icons-material/MoreVert';
import CloseIcon        from '@mui/icons-material/Close';
import FunctionsIcon    from '@mui/icons-material/Functions';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon       from '@mui/icons-material/DeleteOutline';
import { debounce }     from 'lodash';

import { useFieldMetadata }          from '../../../contexts/FieldMetadataContext';
import { useFormContext }            from '../../../contexts/FormContext';
import { useFormLayout }             from '../../../contexts/FormLayoutContext';
import { useFieldEdit }              from '../../../contexts/FieldEditContext';
import { useFormSubmissionSettings } from '../../../contexts/FormSubmissionSettingsContext';
import { useOrgAssetConfig } from '../../../contexts/OrgAssetConfigContext';

import CalculatedFieldEditor from './CalculatedFieldEditor';
import ConfirmDeleteDialog   from './ConfirmDeleteDialog'; 
import SystemFieldGroupsAccordion from './SystemFieldGroupsAccordion';
import { SYSTEM_COLUMNS } from '../../../utils/systemColumns';
import { getProviderColumnSpec } from '../../../utils/providerOutputColumnUtils';
import { getProviderGroupDefinition } from '../../../integrations/providerColumns';


/* ───────────────────────────────────────── helpers */
const buildFromLayout = (layout = {}, meta = {}) =>
  Object.values(layout).flat()
    .filter(f => f.type !== 'submit')
    .map((f, i) => ({
      id          : meta[f.id]?.ufi   ?? f.id,
      label       : meta[f.id]?.label ?? `Field ${i + 1}`,
      visible     : true,
      order       : i,
      isCalculated: false,
    }));

const mergeIntoBase = (base = [], fresh = []) => {
  const next = [...base];
  fresh.forEach(f => {
    const idx = next.findIndex(c => c.id === f.id);
    if (idx === -1) next.push({ ...f, order: next.length });
    else            next[idx] = { ...next[idx], label: f.label };
  });
  return next
  .filter(c =>
    fresh.some(f => f.id === c.id) ||
    c.isCalculated ||
    c.kind === 'virtual' ||
    c.kind === 'system'   // ← keep system rows in the list
  )
  .map((c, i) => ({ ...c, order: i }));

};

/* ───────────────────────────────────────── virtual column helpers (asset columns) */
/* id convention keeps keys stable & discoverable */
const makeVirtualId = (role, ufi, extra = {}) => {
  // AUF virtuals must be unique per (role, attachUfi, aufUfi)
  if (role === 'aufField' && extra?.aufUfi) {
    return `${role}@${ufi}@${extra.aufUfi}`;
  }
  return `${role}@${ufi}`;
};

const hasVirtual = (cols = [], role, ufi, extra = {}) => {
  const id = makeVirtualId(role, ufi, extra);
  return cols.some((c) => {
    if (c?.id === id) return true;
    if (c?.kind !== 'virtual') return false;
    // Match by metadata as well, in case id was persisted earlier
    if (c.role !== role) return false;
    if (c.ufi  !== ufi)  return false;
    if (role === 'aufField') {
      return (c.aufUfi && extra?.aufUfi && c.aufUfi === extra.aufUfi);
    }
    return true;
  });
};

/**
 * ensureVirtual – add a virtual column for the given role/ufi if missing.
 * `extra` may include aufUfi, promoted, provider metadata, etc.
 */
const ensureVirtual = (cols = [], role, ufi, label, extra = {}) => {
  if (hasVirtual(cols, role, ufi, extra)) return cols;
  const id = makeVirtualId(role, ufi, extra);
  const newCol = {
    id,
    label,
    visible: true,
    order : cols.length,
    kind  : 'virtual',
    role,                  // 'thumb' | 'version' | 'assetType' | 'assetId' | 'aufField' | 'providerField'
    ufi,                   // source attach/image field UFI
    ...extra,              // e.g., { aufUfi, promoted } or { provider:{name,key} }
  };
  console.log('[ColumnDraftManager] ➕ add virtual column:', newCol);
  return [...cols, newCol];
};

const dropVirtual = (cols = [], role, ufi, extra = {}) => {
  const id = makeVirtualId(role, ufi, extra);
  const next = cols.filter((c) => {
    if (c?.id === id) return false;
    if (c?.kind !== 'virtual') return true;
    if (c.role !== role || c.ufi !== ufi) return true;
    if (role === 'aufField') {
      // only drop the matching AUF virtual (same aufUfi)
      return !(c.aufUfi && extra?.aufUfi && c.aufUfi === extra.aufUfi);
    }
    return true;
  });
  console.log('[ColumnDraftManager] ➖ drop virtual column:', {
    role, ufi, aufUfi: extra?.aufUfi, id
  });
  return next;
};

// ---------- RECONCILE: drop orphaned/legacy virtual rows ----------
const buildAttachUfiSet = (fieldsMeta = {}) => {
  const s = new Set();
  Object.values(fieldsMeta || {}).forEach((m) => {
    if (m?.ufi && (m.type === 'attach' || m.type === 'image')) s.add(m.ufi);
  });
  return s;
};

const buildValidAufUfiSet = (registry = []) => {
  const s = new Set();
  (registry || []).forEach((def) => {
    if (def?.role === 'aufField' && def.ufi) s.add(def.ufi);
  });
  return s;
};

/**
 * remove legacy/gone virtuals:
 *  - aufField rows with no aufUfi (legacy id 'aufField@<attachUfi>')
 *  - aufField rows whose aufUfi isn’t present in current registry
 *  - any virtual row whose attach ufi no longer exists in layout/metadata
 */
const reconcileVirtuals = (cols = [], registry = [], fieldsMeta = {}) => {
  const attachUfIs   = buildAttachUfiSet(fieldsMeta);
  const validAufUfIs = buildValidAufUfiSet(registry);

  const keep = [];
  let pruned = 0;

  for (const c of cols) {
    if (c?.kind !== 'virtual') { keep.push(c); continue; }

    // Provider output/metric columns are not asset-field virtuals.
    // They may be sourced from calculated fields, URL fields, or future
    // provider mappings, so they must NOT be pruned by attach/image rules.
    const providerSpec = getProviderColumnSpec(c);
    if (providerSpec.isProviderColumn) {
      keep.push({
        ...c,
        kind: 'virtual',
        role: providerSpec.role,
        provider: providerSpec.provider,
        ...(providerSpec.outputKey ? { outputKey: providerSpec.outputKey } : {}),
        ...(providerSpec.metricKey ? { metricKey: providerSpec.metricKey } : {}),
      });
      continue;
    }

    // attach UFI must still exist
    if (!c?.ufi || !attachUfIs.has(c.ufi)) { pruned++; continue; }

    if (c.role === 'aufField') {
      // legacy AUF rows had no aufUfi → prune
      if (!c.aufUfi) { pruned++; continue; }
      // aufUfi must still be offered by the registry → else prune
      if (!validAufUfIs.has(c.aufUfi)) { pruned++; continue; }
    }

    keep.push(c);
  }

  if (pruned > 0) {
    console.log('[ColumnDraftManager] 🧹 reconcileVirtuals → pruned:', pruned);
  }
  // ───────────────────────────────────────────────────────────────
  // REFRESH LABELS for AUF virtuals so AUF renames propagate
  //   • We pull the latest label from the registry (by aufUfi)
  //   • Only updates label text; keeps visibility/order/id
  // ───────────────────────────────────────────────────────────────
  const getAufLabelFromRegistry = (reg = [], aufUfi) => {
    const def = (reg || []).find(d => d?.role === 'aufField' && d.ufi === aufUfi);
    return def?.label || null;
  };

  for (let i = 0; i < keep.length; i++) {
    const c = keep[i];
    if (c.kind === 'virtual' && c.role === 'aufField' && c.aufUfi) {
      const newLabel = getAufLabelFromRegistry(registry, c.aufUfi);
      if (newLabel && newLabel !== c.label) {
        keep[i] = { ...c, label: newLabel };
      }
    }
  }

  // ───────────────────────────────────────────────────────────────
  // REFRESH SUFFIX "(<base attach label>)" for ALL virtual rows
  //   • Base label comes from fieldsMetadata for the attach/image UFI
  //   • Left part comes from:
  //       - AUF def.label (already refreshed above), OR
  //       - Static role label (Version / Type / Asset ID) from registry
  //   • We rebuild: "<left> (<baseLabel>)"
  // ───────────────────────────────────────────────────────────────
  const ufiToBaseLabel = {};
  Object.values(fieldsMeta || {}).forEach(m => {
    if (m?.ufi && (m.type === 'attach' || m.type === 'image')) {
      ufiToBaseLabel[m.ufi] = m.label || 'Attach';
    }
  });

  // helper: get a static label for non-AUF roles from the registry
  const getStaticLabelForRole = (reg = [], role) => {
    // pick the first def for that role; its .label is the left part
    const def = (reg || []).find(d => d?.role === role);
    return def?.label || null;
  };

  for (let i = 0; i < keep.length; i++) {
    const c = keep[i];
    if (!(c?.kind === 'virtual' && c?.ufi)) continue;

    const baseLabel = ufiToBaseLabel[c.ufi] || 'Attach';

    // Determine the left side:
    let leftLabel = null;

    if (c.role === 'aufField' && c.aufUfi) {
      // left already refreshed above to latest AUF label;
      // if that block didn’t change it (same label), use current c.label
      // but strip any old " (…)" suffix safely:
      const idx = String(c.label || '').lastIndexOf(' (');
      leftLabel = idx > 0 ? c.label.slice(0, idx) : c.label;
    } else {
      // static roles: Version / Type / Asset ID
      const staticLeft = getStaticLabelForRole(registry, c.role);
      if (staticLeft) {
        leftLabel = staticLeft;
      } else {
        // fallback: keep existing left part (strip old suffix if present)
        const idx = String(c.label || '').lastIndexOf(' (');
        leftLabel = idx > 0 ? c.label.slice(0, idx) : (c.label || '');
      }
    }

    const rebuilt = `${leftLabel} (${baseLabel})`;
    if (rebuilt !== c.label) {
      keep[i] = { ...c, label: rebuilt };
    }
  }

  return keep.map((c, i) => ({ ...c, order: i }));
};


// Inject one hidden virtual row for each registry def on asset fields.
// Returns [nextCols, injectedCount]
const injectDefaultVirtuals = (cols, registry, fieldsMetadata) => {
  if (!Array.isArray(cols) || !Array.isArray(registry)) return [cols, 0];
  let injected = 0;
  let next = cols;

  // find real attach/image fields by UFI
  const byUfi = Object.values(fieldsMetadata || {})
    .reduce((acc, m) => { if (m?.ufi) acc[m.ufi] = m; return acc; }, {});

  // for each "real" row, add missing virtuals (visible:false)
  cols.forEach(row => {
    const ufi = row?.id;
    const meta = byUfi[ufi];
    const isAsset =
      meta && (meta.type === 'attach' || meta.type === 'image');
    if (!isAsset) return;

    const labelBase = meta?.label || 'Attach';
    const defs = (registry || [])
      .filter(def => def.fieldTypes.includes(meta.type));

    defs.forEach(def => {
      const extra =
        (def.role === 'aufField')
          ? { aufUfi: def.ufi, promoted: !!def.promoted }
          : (def.role === 'providerField'
              ? { provider: def.provider || null }
              : {});

      if (hasVirtual(next, def.role, ufi, extra)) return;

      // add the virtual row (default visible true), then flip it off
      let added = ensureVirtual(
        next,
        def.role,
        ufi,
        `${def.label} (${labelBase})`,
        extra
      );

      if (added !== next) {
        const vId = makeVirtualId(def.role, ufi, extra);
        added = added.map(r =>
          r.id === vId ? { ...r, visible: false } : r
        );
        next = added;
        injected += 1;
      }
    });

  });

  // normalize order after injections
  if (injected > 0) {
    next = next.map((c, i) => ({ ...c, order: i }));
  }
  return [next, injected];
};

// ───────────── System field defaults (UI-only injection; no Firestore writes)
// Source of truth for ids/labels: utils/systemColumns.js (SYSTEM_COLUMNS)
/**
 * ensureSystemDefaultsInjected
 * Adds missing system rows as { kind:'system', visible:false } so they appear
 * under the "System fields" accordion. Ordering is normalized after inserts.
 *
 * @returns [nextCols, injectedCount]
 */
const ensureSystemDefaultsInjected = (cols = []) => {
  if (!Array.isArray(cols)) return [cols, 0];
  let next = cols;
  let injected = 0;

  for (const def of (SYSTEM_COLUMNS || [])) {
    const sysId = def?.id;          // e.g., 'sys:submittedAt'
    const label = def?.label || ''; // human label
    if (!sysId) continue;

    const already = (next || []).some(
      (c) => c?.kind === 'system' && c?.id === sysId
    );
    if (already) continue;

    const row = {
      id     : sysId,
      label  : label,
      visible: false,             // hidden by default → shows in accordion
      order  : next.length,
      kind   : 'system',
      role   : 'system',          // keep a role for grouping/switch logic
    };
    console.log('[ColumnDraftManager] ➕ injected system default:', row);
    next = [...next, row];
    injected += 1;
  }

  if (injected > 0) {
    next = next.map((c, i) => ({ ...c, order: i }));
  }
  return [next, injected];
};


// ───────────── System / asset / provider field groups
// Asset Studio roles remain asset-field dependent.
// Provider output/metric roles are provider-owned and may be sourced from
// calculated fields, URL fields, or future mappings.
const ASSET_ROLES = new Set(['thumb', 'version', 'assetType', 'assetId', 'aufField', 'providerField']);

const formatProviderGroupFallback = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return 'Provider';
  return raw.charAt(0).toUpperCase() + raw.slice(1);
};

const getGroupIdForVirtual = (c) => {
  const providerSpec = getProviderColumnSpec(c);

  if (providerSpec.isProviderColumn) {
    return c?.groupId || providerSpec.provider || 'providers';
  }

  return ASSET_ROLES.has(c?.role) ? 'assetStudio' : 'system';
};

const getGroupLabel = (groupId, column = null) => {
  const providerSpec = getProviderColumnSpec(column);

  if (providerSpec.isProviderColumn) {
    const providerGroup = getProviderGroupDefinition(providerSpec.provider);

    return (
      column?.groupLabel ||
      providerGroup?.groupLabel ||
      formatProviderGroupFallback(providerSpec.provider)
    );
  }

  if (groupId === 'assetStudio') return 'Asset Studio';
  if (groupId === 'system') return 'System fields';

  return formatProviderGroupFallback(groupId);
};

// Ensure core accordions render even if empty, so we preserve the existing UI.
// Provider groups are added only when provider columns exist.
const ensureBothGroups = (byGroup = {}) => {
  const next = { ...byGroup };
  if (!next.system) {
    next.system = { label: getGroupLabel('system'), items: [] };
  }
  if (!next.assetStudio) {
    next.assetStudio = { label: getGroupLabel('assetStudio'), items: [] };
  }
  return next;
};

/* ───────────────────────────────────────── component */
export default function ColumnDraftManager({
  columnOrder = [],
  onChange,
  settingsKey = 'addToSubmissionsTable',
}) {
  /* context */
  const { fieldsMetadata, addOrUpdateCalculated, deleteFieldMetadata } = useFieldMetadata();
  const { layoutConfig }        = useFormLayout();
  const { isFieldChangesReady } = useFieldEdit();
  const {
    submissionSettings,
    updateSettings,
    updateTableSurfaceSettings,
  } = useFormSubmissionSettings();
  const { formId }              = useFormContext();
  // Centralized, org-scoped columns registry (static + AUF + providers), read-only
  const { registry, loading: orgCfgLoading, error: orgCfgError } = useOrgAssetConfig() || { registry: [] };

  /* state */
  const [localCols, setLocalCols] = useState(() => {
    const fallback = buildFromLayout(layoutConfig, fieldsMetadata);
    return columnOrder.length ? mergeIntoBase(columnOrder, fallback) : fallback;
  });
  const [editingId,  setEditingId ] = useState(null);
  const [deleteMode, setDeleteMode] = useState(false);

  /* dialog state */
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [candidateCol, setCandidateCol] = useState(null);     // { id, label, … }

  const tableSurfaceSettings =
    submissionSettings?.[settingsKey] ||
    submissionSettings?.addToSubmissionsTable ||
    {};

  /**
   * Persist column manager changes to the table surface currently being edited.
   *
   * Enterprise surface contract:
   * - addToSubmissionsTable remains the in-form Display Table action.
   * - submissionsPageTable remains the dedicated Submissions Page view.
   * - ColumnDraftManager edits columns only; it must not decide which submit
   *   action is active or disable other submission actions.
   */
  const persistTableSurface = useCallback((patch = {}) => {
    if (typeof updateTableSurfaceSettings === 'function') {
      updateTableSurfaceSettings(settingsKey, patch);
      return;
    }

    // Backward-compatible fallback for older context versions.
    // This should not run once FormSubmissionSettingsContext exposes
    // updateTableSurfaceSettings.
    console.warn(
      '[ColumnDraftManager] updateTableSurfaceSettings unavailable; falling back to updateSettings.',
      {
        settingsKey,
      }
    );
    updateSettings(settingsKey, patch);
  }, [
    settingsKey,
    updateSettings,
    updateTableSurfaceSettings,
  ]);

  /* debounced persist */
  const saveDebouncedRef = useRef(null);

  useEffect(() => {
    saveDebouncedRef.current?.cancel?.();

    saveDebouncedRef.current = debounce(cols => {
      console.log('[ColumnDraftManager] ⏳ save', {
        settingsKey,
        cols,
      });
      persistTableSurface({ columnOrder: cols });
    }, 350);

    return () => {
      saveDebouncedRef.current?.cancel?.();
    };
  }, [
    persistTableSurface,
    settingsKey,
  ]);

  const saveDebounced = useCallback((cols) => {
    saveDebouncedRef.current?.(cols);
  }, []);

  /* sync on layout/meta change */
  useEffect(() => {
    if (!isFieldChangesReady || !layoutConfig) return;
    const fresh = buildFromLayout(layoutConfig, fieldsMetadata);
    setLocalCols(prev => {
      const merged = mergeIntoBase(prev, fresh);
      if (JSON.stringify(prev) !== JSON.stringify(merged)) {
        onChange?.(merged);
        saveDebounced(merged);
      }
      return merged;
    });
  }, [layoutConfig, fieldsMetadata, isFieldChangesReady, onChange, saveDebounced]);

  // After registry is ready, pre-seed hidden virtual rows so they appear
  // as normal rows (reorderable) but remain OFF until you flip row switches.
useEffect(() => {
  if (orgCfgLoading || orgCfgError) return;
  if (!Array.isArray(localCols) || localCols.length === 0) return;


    // 1) Reconcile orphaned/legacy virtuals (AUF removed, attach field removed, or legacy no-aufUfi rows)
    const reconciled = reconcileVirtuals(localCols, registry, fieldsMetadata);

    // 2) Inject any new AUF/provider virtuals (hidden by default)
    const [afterInject, injectedVirtuals] =
      injectDefaultVirtuals(reconciled, registry, fieldsMetadata);

    // 3) Inject System defaults (hidden by default → accordion)
    const [afterSystem, injectedSystem] = ensureSystemDefaultsInjected(afterInject);

    // Only push when something changed (order, adds, prunes, or labels)
    const idsBefore    = localCols.map(c => c.id).join('|');
    const idsAfter     = afterSystem.map(c => c.id).join('|');
    const labelsBefore = localCols.map(c => c.label).join('|');
    const labelsAfter  = afterSystem.map(c => c.label).join('|');

    const changed = (
      idsBefore    !== idsAfter    ||
      labelsBefore !== labelsAfter ||
      injectedVirtuals > 0 ||
      injectedSystem   > 0
    );

    if (changed) {
      push(afterSystem);
      if (injectedVirtuals > 0) {
        console.log('[ColumnDraftManager] pre-seeded hidden virtual rows:', injectedVirtuals);
      }
      if (injectedSystem > 0) {
        console.log('[ColumnDraftManager] pre-seeded hidden system rows:', injectedSystem);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgCfgLoading, orgCfgError, registry, fieldsMetadata]);

  // ───────────────────────────────────────── main visible rows (numbered list only)
  // ───────────────────────────────────────── numbered list:
  // • Regular (layout/calculated) rows → always listed (toggle shows state)
  // • System/Virtual rows → listed only when visible (else they live in accordions)
  const mainRows = React.useMemo(
    () => (localCols || [])
      .filter(c => {
        const k = c?.kind;
        if (k === 'system' || k === 'virtual') return !!c?.visible; // accordion-controlled
        return true; // regular/calculated stay in the list even when visible=false
      })
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [localCols]
  );


  // ───────────────────────────────────────── build accordion groups (UI-only)
  // Hidden rows only:
  // • System fields: kind==='system'   && visible===false
  // • Asset Studio:  kind==='virtual'  && visible===false
  const hiddenGroups = React.useMemo(() => {
    const byGroup = {};
    (localCols || []).forEach(c => {
      if (!c?.visible && (c?.kind === 'system' || c?.kind === 'virtual')) {
        const gid = getGroupIdForVirtual(c); // 'system' | 'assetStudio' | provider group id
        const group = (byGroup[gid] ||= { label: getGroupLabel(gid, c), items: [] });
        group.items.push({ id: c.id, label: c.label, disabled: false });
      }
    });
    return ensureBothGroups(byGroup); // guarantees core accordions show up, even empty
  }, [localCols]);



  /* ------------------------------------------------ single-column deletion */
  const deleteColumn = useCallback(async (col) => {
    if (!col?.id) return;

    console.log('[ColumnDraftManager] deleting column:', col);

    // Virtual columns: drop-only; do not purge real field metadata
    if (col.kind === 'virtual') {
      setLocalCols(prev => {
        const providerSpec = getProviderColumnSpec(col);
        const next = providerSpec.isProviderColumn
          ? (prev || []).filter((candidate) => candidate?.id !== col.id)
          : dropVirtual(prev, col.role, col.ufi, { aufUfi: col.aufUfi });

        onChange?.(next);

        const curWidths =
          tableSurfaceSettings?.columnWidths || {};
        const { [col.id]: _drop, ...trimmed } = curWidths;

        persistTableSurface({
          columnOrder: next,
          columnWidths: trimmed,
        });

        return next;
      });

      if (editingId === col.id) setEditingId(null);
      console.log('[ColumnDraftManager] ✔ virtual column deleted:', col.id);
      return;
    }

    /* 1️⃣ remove from local state */
    const next = localCols.filter(c => c.id !== col.id);
    setLocalCols(next);
    onChange?.(next);

    /* 2️⃣ strip widths & persist order */
    const curWidths =
      tableSurfaceSettings?.columnWidths || {};
    const { [col.id]: _dropped, ...trimmed } = curWidths;
    persistTableSurface({
      columnOrder : next,
      columnWidths: trimmed,
    });

    /* 3️⃣ purge metadata (FieldMetadataContext + storage) */
    deleteFieldMetadata(col.id);


    /* 4️⃣ Firestore clean-up when applicable */
    try {
      if (formId && !formId.startsWith('draft_')) {
        const { deleteFieldFromForm } = await import('../../../utils/firestore');
        await deleteFieldFromForm(col.id, formId);
      }
    } catch (err) {
      console.warn('[ColumnDraftManager] Firestore deleteFieldFromForm failed (draft/offline?):', err);
    }

    /* 5️⃣ reset edit state */
    if (editingId === col.id) setEditingId(null);

    console.log('[ColumnDraftManager] ✔ column deleted:', col.id);
  }, [
    localCols,
    tableSurfaceSettings,
    onChange,
    persistTableSurface,
    deleteFieldMetadata,
    formId,
    editingId,
  ]);

  // (UI-only) Temporary toggle sink; we will wire this to show/hide once contexts are in.
  const noopToggle = useCallback((id, next) => {
    console.log('[ColumnDraftManager] (UI-only) accordion onToggle →', { id, next });
    // intentionally no state updates yet; this is just to validate accordion UI
  }, []);
  
  /* misc helpers */
  const push = useCallback((colsOrFn) => {
    const cols = typeof colsOrFn === 'function' ? colsOrFn(localCols) : colsOrFn;
    setLocalCols(cols);
    onChange?.(cols);
    saveDebounced(cols);
  }, [localCols, onChange, saveDebounced]);

  // map a row id to its index inside localCols
  const idxInLocal = useCallback(
    (id) => (localCols || []).findIndex(c => c?.id === id),
    [localCols]
  );

  // Id-based visibility toggle (works for regular, system, and virtual rows)
  const toggleVisById = useCallback((id) => {
    const i = idxInLocal(id);
    if (i === -1) return;
    push(localCols.map((c, j) => (j === i ? { ...c, visible: !c.visible } : c)));
  }, [idxInLocal, localCols, push]);

  // Reorder by positions within the **visible** main list only
  const reorder = useCallback((fromMain, toMain) => {
    const movingId = mainRows[fromMain]?.id;
    const targetId = mainRows[toMain]?.id;
    if (!movingId || !targetId) return;

    const fromLocal = idxInLocal(movingId);
    const toLocal   = idxInLocal(targetId);
    if (fromLocal === -1 || toLocal === -1) return;

    const clone = [...localCols];
    const [moved] = clone.splice(fromLocal, 1);
    clone.splice(toLocal, 0, moved);
    push(clone.map((c, i) => ({ ...c, order: i })));
  }, [mainRows, idxInLocal, localCols, push]);

  // For accordion: ON → append to end, OFF → hide
  const showFieldById = useCallback((id) => {
    const maxOrder = Math.max(-1, ...(localCols || []).map(c => c.order ?? -1));
    const next = (localCols || [])
      .map(c => c.id === id ? ({ ...c, visible: true, order: maxOrder + 1 }) : c)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((c, i) => ({ ...c, order: i }));
    push(next);
  }, [localCols, push]);

  const hideFieldById = useCallback((id) => {
    push((localCols || []).map(c => c.id === id ? { ...c, visible: false } : c));
  }, [localCols, push]);


  /* header menu */
  const [menuEl, setMenuEl] = useState(null);
  const openMenu  = e => setMenuEl(e.currentTarget);
  const closeMenu = () => setMenuEl(null);

  const addCalculated = () => {
    closeMenu();
    const id = `calc_${Date.now()}`;
    push([...localCols, {
      id,
      label       : 'New calculated field',
      visible     : true,
      order       : localCols.length,
      isCalculated: true,
      formula : 'TEMPLATE',
      delimiter: ' ',
      fields  : [],
      template: '',
    }]);
    setEditingId(id);
  };

  const toggleDeleteMode = () => {
    closeMenu();
    setDeleteMode(prev => !prev);
  };

  /* cleanup */
  useEffect(() => () => saveDebouncedRef.current?.cancel?.(), []);

  /* ───────────────────────────────────────── render */
  return (
    <Box sx={{ p: 1 }}>
      {/* ───── title + menu */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, flexGrow: 1, color: deleteMode ? 'error.main' : 'inherit' }}
        >
          {deleteMode
            ? 'Select to Delete Columns'
            : settingsKey === 'submissionsPageTable'
              ? 'Columns in Submissions Page'
              : 'Columns in Display Table'}
        </Typography>

        {deleteMode ? (
          <IconButton size="small" onClick={() => setDeleteMode(false)} sx={{ p: 0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        ) : (
          <IconButton size="small" onClick={openMenu} sx={{ p: 0.5 }}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
        )}

        <Menu
          anchorEl={menuEl}
          open={Boolean(menuEl)}
          onClose={closeMenu}
          elevation={2}
          MenuListProps={{ dense: true }}
          PaperProps={{ sx: { '& .MuiMenuItem-root': { fontSize: '0.75rem', py: 0.75 } } }}
        >
          <MenuItem onClick={addCalculated}>
            <FunctionsIcon sx={{ fontSize: '1rem', mr: 1 }} />
            New calculated field
          </MenuItem>
          <Divider />
          <MenuItem onClick={toggleDeleteMode}>
            <DeleteIcon sx={{ fontSize: '1rem', mr: 1, color: deleteMode ? 'error.main' : 'inherit' }} />
            {deleteMode ? 'Exit delete mode' : 'Delete'}
          </MenuItem>
        </Menu>
      </Box>

      {/* ───── rows */}
      {mainRows.map((col, idx) => {
        const inEdit = col.id === editingId;
        return (
          <Box
            key={col.id}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75,
              bgcolor: inEdit ? 'primary.light' : undefined,
              borderRadius: inEdit ? 1 : 0,
              '&:not(:last-of-type)': { borderBottom: '1px solid #f0f0f0' },
            }}
          >
            <FormControl variant="standard" size="small" sx={{
              minWidth: 40,
              '& .MuiSelect-select': { fontSize: '0.75rem', lineHeight: 1.2, p: '2px 6px 2px 4px' },
              '& .MuiInput-underline:before': { borderBottom: 'none' },
            }}>
              <Select
                value={idx}
                onChange={e => reorder(idx, Number(e.target.value))}
                disableUnderline
                MenuProps={{
                  MenuListProps: { dense: true },
                  PaperProps: { sx: { '& .MuiMenuItem-root': { fontSize: '0.75rem', minHeight: 24, px: 1.5 } } },
                }}
              >
                {mainRows.map((_, i) => (
                  <MenuItem key={i} value={i}>{i + 1}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Typography variant="body2" sx={{ flexGrow: 1, fontSize: '0.75rem' }}>
              {col.label}
            </Typography>

            <Switch size="small" checked={!!col.visible} onChange={() => toggleVisById(col.id)} />

            {col.isCalculated && (
              <IconButton
                size="small"
                sx={{ p: 0.5 }}
                onClick={() => {
                  if (deleteMode) {
                    setCandidateCol(col);
                    setConfirmOpen(true);
                  } else {
                    setEditingId(col.id);
                  }
                }}
              >
                {deleteMode ? (
                  <DeleteIcon sx={{ fontSize: '0.85rem', color: 'error.main' }} />
                ) : (
                  <EditOutlinedIcon sx={{ fontSize: '0.85rem' }} />
                )}
              </IconButton>
            )}
          </Box>
        );
      })}

      {/* ───── system field groups accordion (UI-only for now; toggles are no-op) */}
      <SystemFieldGroupsAccordion
        groups={hiddenGroups}
        activeIds={new Set(
          (localCols || [])
            .filter(c => c?.visible && (c?.kind === 'virtual' || c?.kind === 'system'))
            .map(c => c.id)
        )}
        onToggle={(id, next) => (next ? showFieldById(id) : hideFieldById(id))}
        defaultExpanded={['system', 'assetStudio']}
        loading={orgCfgLoading}
        error={orgCfgError ? 'Organization config failed to load.' : null}
      />

      {/* ───── drawer (disabled in delete-mode) */}
      {!deleteMode && (
        <Drawer
          anchor="right"
          open={Boolean(editingId)}
          onClose={() => setEditingId(null)}
          PaperProps={{ sx: { width: 320, p: 2 } }}
        >
          <CalculatedFieldEditor
            meta={localCols.find(c => c.id === editingId) || {}}
            onClose={() => setEditingId(null)}
            onSave={(nextMeta) => {
              push(localCols.map(c =>
                c.id === editingId ? { ...c, ...nextMeta, isCalculated: true } : c
              ));
              const ufi = nextMeta.ufi || nextMeta.id;
              addOrUpdateCalculated(ufi, {
                ufi,
                label    : nextMeta.label || 'Calculated',
                type     : 'calculated',
                formula  : nextMeta.formula,
                delimiter: nextMeta.delimiter,
                template : nextMeta.template,
              });
              setEditingId(null);
            }}
          />
        </Drawer>
      )}

      {/* ───── confirm-delete dialog */}
      <ConfirmDeleteDialog
        open={confirmOpen}
        columnLabel={candidateCol?.label || ''}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false);
          if (candidateCol) deleteColumn(candidateCol);
          setCandidateCol(null);
        }}
      />
    </Box>
  );
}
